import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { LoadingState, EmptyState, PageHeader } from "@/components/features/states";
import {
  Store as StoreIcon,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Star,
  Package,
  ShoppingBag,
  Clock,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { currencySymbol } from "@/lib/format";

const STATUS_FILTERS = [
  { key: "pending", labelKey: "admin.stores.statusPending" },
  { key: "approved", labelKey: "admin.stores.statusApproved" },
  { key: "suspended", labelKey: "admin.stores.statusSuspended" },
  { key: "rejected", labelKey: "admin.stores.statusRejected" },
];

export function AdminStoresView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [reviewStore, setReviewStore] = useState<any | null>(null);
  const [action, setAction] = useState<"approve" | "reject" | "suspend">("approve");
  const [reason, setReason] = useState("");
  const [tier, setTier] = useState<"basic" | "premium" | "flagship">("basic");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stores", status],
    queryFn: () => api.admin.stores({ status }),
  });
  const stores = data?.data?.stores ?? [];

  const review = useMutation({
    mutationFn: () =>
      api.admin.reviewStore(reviewStore.id, {
        action,
        reason: reason || undefined,
        commissionTier: action === "approve" ? tier : undefined,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(
          action === "approve"
            ? t("admin.storeApproved")
            : action === "reject"
            ? t("admin.storeRejected")
            : t("admin.storeSuspended")
        );
        qc.invalidateQueries({ queryKey: ["admin", "stores"] });
        setReviewStore(null);
        setReason("");
      } else {
        toast.error(res.error);
      }
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.merchantOnboarding")}
        subtitle={t("admin.storeApplications")}
      />

      {/* Status filter tabs */}
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="w-full">
          {STATUS_FILTERS.map((f) => (
            <TabsTrigger key={f.key} value={f.key} className="flex-1">
              {t(f.labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Stores list */}
      {isLoading ? (
        <LoadingState />
      ) : stores.length === 0 ? (
        <EmptyState
          icon={<StoreIcon className="h-8 w-8" />}
          title={t("admin.noStores")}
          description={t("admin.stores.noStoresForStatus", { status: t(`admin.stores.status${status.charAt(0).toUpperCase()}${status.slice(1)}`) })}
        />
      ) : (
        <div className="space-y-3">
          {stores.map((store: any) => (
            <Card key={store.id}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex gap-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                    {store.imageUrl ? (
                      <img src={store.imageUrl} alt={store.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <StoreIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="line-clamp-1 font-bold">{store.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {store.owner?.name} · {store.owner?.email}
                        </p>
                        {store.description && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {store.description}
                          </p>
                        )}
                      </div>
                      <StatusBadge status={store.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <Badge variant="outline" className="capitalize">{store.category}</Badge>
                      <Badge variant="outline" className="capitalize">
                        {store.commissionTier} ({store.commissionRate}%)
                      </Badge>
                      {store.owner?.trust?.status === "verified" && (
                        <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                          {t("admin.stores.idVerified")}
                        </Badge>
                      )}
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        <Package className="h-3 w-3" /> {store._count?.products || 0}
                      </span>
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        <ShoppingBag className="h-3 w-3" /> {store._count?.jobs || 0}
                      </span>
                    </div>
                    {/* Review actions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {store.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setReviewStore(store);
                              setAction("approve");
                              setTier(store.commissionTier || "basic");
                              setReason("");
                            }}
                          >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {t("admin.approveStore")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setReviewStore(store);
                              setAction("reject");
                              setReason("");
                            }}
                          >
                            <XCircle className="mr-1 h-3.5 w-3.5" /> {t("admin.rejectStore")}
                          </Button>
                        </>
                      )}
                      {store.status === "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReviewStore(store);
                            setAction("suspend");
                            setReason("");
                          }}
                        >
                          <PauseCircle className="mr-1 h-3.5 w-3.5" /> {t("admin.suspendStore")}
                        </Button>
                      )}
                      {store.status === "rejected" && store.rejectionReason && (
                        <p className="text-xs text-red-600">
                          {t("admin.rejectionReason")}: {store.rejectionReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review sheet */}
      <Sheet open={!!reviewStore} onOpenChange={(v) => !v && setReviewStore(null)}>
        <SheetContent side="bottom" className="mx-auto max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {action === "approve" ? t("admin.approveStore") : action === "reject" ? t("admin.rejectStore") : t("admin.suspendStore")}
            </SheetTitle>
          </SheetHeader>
          {reviewStore && (
            <div className="mt-4 space-y-4">
              <Card>
                <CardContent className="p-3">
                  <p className="font-bold">{reviewStore.name}</p>
                  <p className="text-xs text-muted-foreground">{reviewStore.owner?.email}</p>
                  <p className="mt-1 text-xs">{reviewStore.description}</p>
                </CardContent>
              </Card>

              {action === "approve" && (
                <div className="space-y-2">
                  <Label>{t("merchant.commissionTier")}</Label>
                  <div className="space-y-2">
                    {[
                      { key: "basic", label: t("merchant.tierBasic") },
                      { key: "premium", label: t("merchant.tierPremium") },
                      { key: "flagship", label: t("merchant.tierFlagship") },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setTier(opt.key as any)}
                        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                          tier === opt.key ? "border-primary bg-primary/5" : "border-border"
                        }`}
                      >
                        <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${tier === opt.key ? "border-primary" : "border-muted-foreground/40"}`}>
                          {tier === opt.key && <div className="h-2 w-2 rounded-full bg-primary" />}
                        </div>
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {action !== "approve" && (
                <div className="space-y-1.5">
                  <Label>{t("admin.rejectionReason")} *</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder={action === "suspend" ? t("admin.stores.reasonForSuspension") : t("admin.stores.reasonForRejection")}
                  />
                </div>
              )}

              <Button
                className="w-full"
                size="lg"
                variant={action === "approve" ? "default" : "destructive"}
                disabled={action !== "approve" && !reason}
                onClick={() => review.mutate()}
              >
                {review.isPending
                  ? t("common.loading")
                  : action === "approve"
                  ? t("admin.approveStore")
                  : action === "reject"
                  ? t("admin.rejectStore")
                  : t("admin.suspendStore")}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const config: Record<string, { icon: React.ElementType; className: string; labelKey?: string }> = {
    pending: { icon: Clock, className: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200", labelKey: "admin.stores.statusPending" },
    approved: { icon: CheckCircle2, className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200", labelKey: "admin.stores.statusApproved" },
    suspended: { icon: PauseCircle, className: "bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-200", labelKey: "admin.stores.statusSuspended" },
    rejected: { icon: XCircle, className: "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200", labelKey: "admin.stores.statusRejected" },
  };
  const c = config[status] || { icon: AlertCircle, className: "bg-muted text-muted-foreground" };
  const Icon = c.icon;
  return (
    <Badge className={c.className}>
      <Icon className="mr-0.5 h-3 w-3" /> {c.labelKey ? t(c.labelKey) : status}
    </Badge>
  );
}
